import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, TextField, MenuItem, Switch, FormControlLabel, Typography,
  Alert, CircularProgress, InputAdornment, Chip, Stack, Paper, FormControl, Select,
  Link, IconButton, Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import profileApi from '../profileApi';
import { isFirebaseSms, initRecaptcha, sendPhoneOtp, verifyPhoneOtp } from '../../../services/firebase';

const GENDERS = ['Male', 'Female', 'Non-binary', 'Prefer not to say'];

// ── Inline verified-change widget ──────────────────────────────────────────
function VerifiedChangeField({ label, currentValue, onSendOtp, onConfirm, onChanged,
  inputType = 'text', inputMode, placeholder, helperText, currentCountryCode = '+44', countryCodeOptions = null }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('input'); // 'input' | 'otp'
  const [newValue, setNewValue] = useState('');
  const [newCountryCode, setNewCountryCode] = useState(currentCountryCode || '+44');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSend = async () => {
    setError('');
    setLoading(true);
    try {
      await onSendOtp(newValue.trim(), newCountryCode);
      setStep('otp');
      setOtp('');
      setCooldown(30);
    } catch (err) {
      setError(err.message || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await onConfirm(newValue.trim(), otp, newCountryCode);
      onChanged?.(result);
      setOpen(false);
      setStep('input');
      setNewValue('');
      setOtp('');
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    try {
      await onSendOtp(newValue.trim(), newCountryCode);
      setCooldown(30);
    } catch (err) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
        <Box sx={{ display: countryCodeOptions ? 'grid' : 'block', gridTemplateColumns: { xs: '90px 1fr', sm: '110px 1fr' }, gap: 0, flex: 1 }}>
          {countryCodeOptions && (
            <FormControl fullWidth>
              <Select value={currentCountryCode || '+44'} readOnly sx={{
                height: 56, bgcolor: 'grey.100',
                '& .MuiOutlinedInput-notchedOutline': { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
              }}>
                {countryCodeOptions.map(code => <MenuItem key={code} value={code}>{code}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <TextField
            label={label}
            value={currentValue || ''}
            fullWidth
            InputProps={{ readOnly: true }}
            sx={{
              ml: countryCodeOptions ? '-1px' : 0,
              '& .MuiInputBase-root': { bgcolor: 'grey.100' },
              '& .MuiOutlinedInput-notchedOutline': countryCodeOptions ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } : {},
            }}
          />
          {helperText && (
            <Typography variant="caption" color="text.secondary" sx={{ gridColumn: countryCodeOptions ? '1 / -1' : 'auto', mt: 0.5, px: '14px' }}>
              {helperText}
            </Typography>
          )}
        </Box>
        <Button variant="outlined" size="small" onClick={() => { setOpen(o => !o); setStep('input'); setError(''); setNewCountryCode(currentCountryCode || '+44'); }}
          sx={{ mt: { xs: 0, sm: 1 }, flexShrink: 0, alignSelf: { xs: 'flex-end', sm: 'auto' } }}>
          {open ? 'Cancel' : currentValue ? 'Change' : 'Add'}
        </Button>
      </Stack>

      {open && (
        <Box sx={{ mt: 1.5, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          {step === 'input' ? (
            <Stack spacing={1.5}>
              <Box sx={{ display: countryCodeOptions ? 'grid' : 'block', gridTemplateColumns: { xs: '90px 1fr', sm: '110px 1fr' }, gap: 0 }}>
                {countryCodeOptions && (
                  <FormControl fullWidth size="small" disabled={loading}>
                    <Select value={newCountryCode} onChange={e => setNewCountryCode(e.target.value)} sx={{
                      '& .MuiOutlinedInput-notchedOutline': { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
                    }}>
                      {countryCodeOptions.map(code => <MenuItem key={code} value={code}>{code}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                <TextField
                  label={`New ${label}`}
                  type={inputType}
                  value={newValue}
                  onChange={e => setNewValue(inputMode === 'numeric'
                    ? e.target.value.replace(/\D/g, '') : e.target.value)}
                  fullWidth
                  size="small"
                  disabled={loading}
                  placeholder={placeholder}
                  inputProps={inputMode ? { inputMode } : {}}
                  sx={{
                    ml: countryCodeOptions ? '-1px' : 0,
                    '& .MuiOutlinedInput-notchedOutline': countryCodeOptions ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } : {},
                  }}
                />
              </Box>
              <Button variant="contained" size="small" onClick={handleSend}
                disabled={loading || !newValue.trim()}>
                {loading ? <CircularProgress size={16} color="inherit" /> : 'Send verification code'}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <Typography variant="caption" color="text.secondary">
                Code sent to <strong>{countryCodeOptions ? `${newCountryCode}${newValue}` : newValue}</strong>
              </Typography>
              <TextField
                label="6-digit code"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                fullWidth
                size="small"
                disabled={loading}
                inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" size="small" onClick={handleConfirm}
                  disabled={loading || otp.length !== 6} sx={{ flex: 1 }}>
                  {loading ? <CircularProgress size={16} color="inherit" /> : 'Verify & Save'}
                </Button>
                <Button variant="text" size="small" onClick={handleResend}
                  disabled={cooldown > 0 || loading}>
                  {cooldown > 0 ? `${cooldown}s` : 'Resend'}
                </Button>
                <Button variant="text" size="small" onClick={() => setStep('input')}>← Back</Button>
              </Stack>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}

const BasicDetails = ({ data, onChange, dirty, saving, onPhoneAdded, onEmailChanged, readOnly = false, isOrgContext = false }) => {
  const isEmailOrGoogleUser = !data.has_phone;
  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopyPublicLink = async () => {
    const link = `${window.location.origin}/profile/${data.username}`;
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } catch { /* ignore */ }
  };

  // Pre-warm reCAPTCHA when this tab is shown.
  useEffect(() => {
    if (isFirebaseSms() && !readOnly) initRecaptcha();
  }, [readOnly]);

  // Firebase-aware phone change handlers.
  const handleFirebasePhoneSend = async (newPhone, countryCode) => {
    await sendPhoneOtp(`${countryCode}${newPhone.replace(/\D/g, '')}`);
  };
  const handleFirebasePhoneConfirm = async (_newPhone, otp) => {
    const idToken = await verifyPhoneOtp(otp);
    return profileApi.confirmPhoneChangeFirebase(idToken);
  };
  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const [usernameMsg, setUsernameMsg] = useState('');
  const [usernameTimeout, setUsernameTimeout] = useState(null);

  const nameParts = (data.customer_name || '').split(' ');
  const firstName = data.first_name || nameParts[0] || '';
  const lastName = data.last_name || nameParts.slice(1).join(' ') || '';

  const handleNameChange = (part, value) => {
    if (readOnly) return;
    const newFirst = part === 'first' ? value : firstName;
    const newLast = part === 'last' ? value : lastName;
    onChange('customer_name', `${newFirst} ${newLast}`.trim());
    onChange(part === 'first' ? 'first_name' : 'last_name', value);
  };

  const handleUsernameChange = (value) => {
    onChange('username', value);
    if (usernameTimeout) clearTimeout(usernameTimeout);
    if (!value || value.length < 3) {
      setUsernameStatus(null);
      setUsernameMsg('');
      return;
    }
    setUsernameStatus('checking');
    const t = setTimeout(async () => {
      try {
        const res = await profileApi.checkUsername(value);
        setUsernameStatus(res.available ? 'available' : 'taken');
        setUsernameMsg(res.message);
      } catch {
        setUsernameStatus(null);
      }
    }, 600);
    setUsernameTimeout(t);
  };

  const usernameAdornment = {
    checking: <CircularProgress size={16} />,
    available: <CheckCircleIcon color="success" fontSize="small" />,
    taken: <CancelIcon color="error" fontSize="small" />,
  }[usernameStatus];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Org context: single "Organisation Name" field instead of first/last name */}
      {isOrgContext ? (
        <TextField
          label="Organisation Name"
          value={data.customer_name || ''}
          onChange={e => { if (!readOnly) onChange('customer_name', e.target.value); }}
          fullWidth
          InputProps={{ readOnly }}
          sx={readOnly ? { '& .MuiInputBase-root': { bgcolor: 'grey.100' } } : {}}
        />
      ) : (
        /* Personal: first + last name */
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label="First Name"
            value={firstName}
            onChange={e => handleNameChange('first', e.target.value)}
            fullWidth
          />
          <TextField
            label="Last Name"
            value={lastName}
            onChange={e => handleNameChange('last', e.target.value)}
            fullWidth
          />
        </Box>
      )}

      {/* Gender + DOB — personal only */}
      {!isOrgContext && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            select
            label="Gender"
            value={data.gender || ''}
            onChange={e => onChange('gender', e.target.value)}
            fullWidth
          >
            {GENDERS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
          </TextField>
          <TextField
            label="Date of Birth"
            type="date"
            value={data.dob || ''}
            onChange={e => onChange('dob', e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      )}

      {/* Email */}
      {readOnly ? (
        <TextField
          label={isOrgContext ? 'Contact Email' : 'Email'}
          value={data.email || ''}
          fullWidth
          InputProps={{ readOnly: true }}
          sx={{ '& .MuiInputBase-root': { bgcolor: 'grey.100' } }}
        />
      ) : (
        <VerifiedChangeField
          label={isOrgContext ? 'Contact Email' : 'Email'}
          currentValue={data.email}
          onSendOtp={(newEmail) => profileApi.sendEmailChangeOtp(newEmail)}
          onConfirm={(newEmail, otp) => profileApi.confirmEmailChange(newEmail, otp)}
          onChanged={(result) => onEmailChanged?.(result.email)}
          inputType="email"
          placeholder="new@example.com"
          helperText={
            isEmailOrGoogleUser
              ? 'Verification code sent to new address - changing this updates your sign-in email'
              : 'Verification code sent to the new address'
          }
        />
      )}

      {/* Phone */}
      {readOnly ? (
        <TextField
          label={isOrgContext ? 'Contact Phone' : 'Phone Number'}
          value={data.phone ? `${data.country_code || '+44'} ${data.phone}` : ''}
          fullWidth
          InputProps={{ readOnly: true }}
          sx={{ '& .MuiInputBase-root': { bgcolor: 'grey.100' } }}
        />
      ) : (
        <>
          <VerifiedChangeField
            label={isOrgContext ? 'Contact Phone' : 'Phone Number'}
            currentValue={data.phone}
            currentCountryCode={data.country_code || '+44'}
            countryCodeOptions={['+44', '+91']}
            onSendOtp={isFirebaseSms()
              ? handleFirebasePhoneSend
              : (newPhone, countryCode) => profileApi.sendPhoneChangeOtp(newPhone, countryCode)}
            onConfirm={isFirebaseSms()
              ? handleFirebasePhoneConfirm
              : (newPhone, otp, countryCode) => profileApi.confirmPhoneChange(newPhone, otp, countryCode)}
            onChanged={(result) => onPhoneAdded?.(result.phonenumber, result.country_code)}
            inputMode="numeric"
            placeholder="10-digit mobile number"
            helperText={
              !data.has_phone
                ? 'Optional - add a phone number to enable SMS sign-in'
                : 'Verification code sent via SMS - changing this updates your sign-in phone'
            }
          />
        </>
      )}

      {/* Public profile */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>Public Profile</Typography>
            <Typography variant="caption" color="text.secondary">
              {isOrgContext
                ? 'Allow anyone to view this organisation via a public link'
                : 'Allow anyone to view your basic profile via a public link'}
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            {data.is_public
              ? <Chip icon={<PublicIcon />} label="Public" color="success" size="small" />
              : <Chip icon={<LockIcon />} label="Private" size="small" />
            }
            <Switch
              checked={!!data.is_public}
              onChange={e => { if (!readOnly) onChange('is_public', e.target.checked); }}
              color="primary"
              disabled={readOnly}
            />
          </Stack>
        </Stack>

        {data.is_public && (
          <TextField
            label="Username"
            value={data.username || ''}
            onChange={e => { if (!readOnly) handleUsernameChange(e.target.value.toLowerCase()); }}
            fullWidth
            placeholder="e.g. john_doe"
            helperText={usernameMsg || 'Letters, numbers, underscores and dots only'}
            error={usernameStatus === 'taken'}
            InputProps={{
              readOnly,
              endAdornment: usernameAdornment ? (
                <InputAdornment position="end">{usernameAdornment}</InputAdornment>
              ) : null,
              startAdornment: <InputAdornment position="start">@</InputAdornment>,
            }}
            sx={{ mt: 1, ...(readOnly && { '& .MuiInputBase-root': { bgcolor: 'grey.100' } }) }}
          />
        )}

        {data.is_public && data.username && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Public link:{' '}
              <Link
                component={RouterLink}
                to={`/profile/${data.username}`}
                underline="hover"
              >
                {window.location.origin}/profile/{data.username}
              </Link>
            </Typography>
            <Tooltip title={linkCopied ? 'Copied!' : 'Copy profile link'}>
              <IconButton size="small" onClick={handleCopyPublicLink}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default BasicDetails;
